/**
 * Canonical **`public.station.id`** sort order for SGS production calendar batch cards
 * and Manufacturing reporting → Station WIP. Keep aligned with
 * **`ProductionCalendarPage`** station rows (tier → flow rank → count desc → id).
 */

/** Filled from public.station.sort_order. Missing ids sort after known rows. */
const sortOrderByStationId = new Map<number, number>();
const sortOrderByStationName = new Map<string, number>();

export function installPublicStationSortOrder(
  stations: readonly { id: number; name?: string; sortOrder: number | null }[],
): void {
  sortOrderByStationId.clear();
  sortOrderByStationName.clear();
  for (const station of stations) {
    if (station.sortOrder == null) continue;
    sortOrderByStationId.set(station.id, station.sortOrder);
    if (station.name != null && station.name.trim() !== "") {
      sortOrderByStationName.set(station.name.trim().toLowerCase(), station.sortOrder);
    }
  }
}

export function publicStationSortOrderForName(name: string): number | null {
  return sortOrderByStationName.get(name.trim().toLowerCase()) ?? null;
}

/** Ids used before public.station.sort_order has loaded. Sort itself uses sort_order. */
export const SGS_QUALITY_CONTROL_STATION_ID = 9;
export const SGS_PACK_OUT_STATION_ID = 14;

export function sgsStationFlowRank(stationId: number): number {
  return sortOrderByStationId.get(stationId) ?? 10000 + stationId;
}

export interface SgsProductionCalendarStationSortRow {
  stationId: number | null;
  isTerminal: boolean;
  /** When set, breaks ties the same way as production calendar batch cards (higher count first). */
  deviceCount?: number;
}

/** Same ordering as production calendar `stationLines` sort; null `stationId` sorts first (Unassigned / not started). */
export function compareSgsProductionCalendarStationOrder(
  left: SgsProductionCalendarStationSortRow,
  right: SgsProductionCalendarStationSortRow,
): number {
  if (left.stationId == null && right.stationId != null) return -1;
  if (left.stationId != null && right.stationId == null) return 1;
  if (left.stationId == null || right.stationId == null) return 0;

  const leftId = left.stationId;
  const rightId = right.stationId;
  const flowDiff = sgsStationFlowRank(leftId) - sgsStationFlowRank(rightId);
  if (flowDiff !== 0) return flowDiff;

  const leftCount = left.deviceCount ?? 0;
  const rightCount = right.deviceCount ?? 0;
  if (leftCount !== rightCount) return rightCount - leftCount;

  return leftId - rightId;
}
